/**
 * Hooks for managing adventure collections
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { adventureCollectionsApi } from '@api/endpoints/adventureCollections'
import type {
  CreateAdventureCollectionDto,
  UpdateAdventureCollectionDto,
  CreateCollectionBadgeDto,
  CreateCollectionAchievementDto,
  UpdateCollectionAchievementDto,
  AddAchievementAdventureDto,
  AddCollectionMaintainerRequest,
} from '@api/types/adventureCollection'

// ===================
// QUERY HOOKS
// ===================

/**
 * Fetch current user's collections
 */
export function useMyCollections() {
  return useQuery({
    queryKey: ['my-collections'],
    queryFn: () => adventureCollectionsApi.getMyCollections(),
  })
}

/**
 * Fetch a single collection by ID
 */
export function useCollection(id: string | undefined) {
  return useQuery({
    queryKey: ['collection', id],
    queryFn: () => adventureCollectionsApi.get(id!),
    enabled: !!id,
  })
}

/**
 * Fetch public collections
 */
export function usePublicCollections(page = 1, pageSize = 20) {
  return useQuery({
    queryKey: ['public-collections', page, pageSize],
    queryFn: () => adventureCollectionsApi.getPublicCollections(page, pageSize),
  })
}

/**
 * Fetch adventures available to add to achievements
 */
export function useAvailableAdventures(collectionId: string | undefined) {
  return useQuery({
    queryKey: ['available-adventures', collectionId],
    queryFn: () => adventureCollectionsApi.getAvailableAdventures(collectionId!),
    enabled: !!collectionId,
  })
}

// ===================
// COLLECTION PROGRESS HOOKS
// ===================

/**
 * Get progress on a specific collection
 */
export function useCollectionProgress(collectionId: string | undefined) {
  return useQuery({
    queryKey: ['collection-progress', collectionId],
    queryFn: () => adventureCollectionsApi.getCollectionProgress(collectionId!),
    enabled: !!collectionId,
  })
}

/**
 * Get current user's progress on all collections
 */
export function useMyCollectionProgress() {
  return useQuery({
    queryKey: ['my-collection-progress'],
    queryFn: () => adventureCollectionsApi.getMyCollectionProgress(),
  })
}

// ===================
// MUTATION HOOKS
// ===================

/**
 * Create a new collection
 */
export function useCreateCollection() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (data: CreateAdventureCollectionDto) =>
      adventureCollectionsApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['my-collections'] })
    },
  })
}

/**
 * Update a collection
 */
export function useUpdateCollection() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateAdventureCollectionDto }) =>
      adventureCollectionsApi.update(id, data),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['collection', result.id] })
      queryClient.invalidateQueries({ queryKey: ['my-collections'] })
    },
  })
}

/**
 * Delete a collection
 */
export function useDeleteCollection() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: string) => adventureCollectionsApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['my-collections'] })
    },
  })
}

// ===================
// BADGE HOOKS
// ===================

/**
 * Set/update a badge for a collection (uses PUT)
 */
export function useSetCollectionBadge() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({
      collectionId,
      data,
    }: {
      collectionId: string
      data: CreateCollectionBadgeDto
    }) => adventureCollectionsApi.setBadge(collectionId, data),
    onSuccess: (_result, variables) => {
      queryClient.invalidateQueries({ queryKey: ['collection', variables.collectionId] })
    },
  })
}

/**
 * Delete a badge
 */
export function useDeleteCollectionBadge() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (collectionId: string) =>
      adventureCollectionsApi.deleteBadge(collectionId),
    onSuccess: (_result, collectionId) => {
      queryClient.invalidateQueries({ queryKey: ['collection', collectionId] })
    },
  })
}

/**
 * Get current user's earned badges
 */
export function useMyBadges() {
  return useQuery({
    queryKey: ['my-badges'],
    queryFn: () => adventureCollectionsApi.getMyBadges(),
  })
}

// ===================
// ACHIEVEMENT QUERY HOOKS
// ===================

/**
 * Fetch all achievements for a collection
 */
export function useCollectionAchievements(collectionId: string | undefined) {
  return useQuery({
    queryKey: ['collection-achievements', collectionId],
    queryFn: () => adventureCollectionsApi.getAchievements(collectionId!),
    enabled: !!collectionId,
  })
}

/**
 * Fetch a single achievement with adventures
 */
export function useCollectionAchievement(collectionId: string | undefined, achievementId: string | undefined) {
  return useQuery({
    queryKey: ['collection-achievement', collectionId, achievementId],
    queryFn: () => adventureCollectionsApi.getAchievement(collectionId!, achievementId!),
    enabled: !!collectionId && !!achievementId,
  })
}

// ===================
// ACHIEVEMENT MUTATION HOOKS
// ===================

/**
 * Create a collection achievement
 */
export function useCreateCollectionAchievement() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({
      collectionId,
      data,
    }: {
      collectionId: string
      data: CreateCollectionAchievementDto
    }) => adventureCollectionsApi.createAchievement(collectionId, data),
    onSuccess: (_result, variables) => {
      queryClient.invalidateQueries({ queryKey: ['collection-achievements', variables.collectionId] })
      queryClient.invalidateQueries({ queryKey: ['collection', variables.collectionId] })
    },
  })
}

/**
 * Update a collection achievement
 */
export function useUpdateCollectionAchievement() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({
      collectionId,
      achievementId,
      data,
    }: {
      collectionId: string
      achievementId: string
      data: UpdateCollectionAchievementDto
    }) => adventureCollectionsApi.updateAchievement(collectionId, achievementId, data),
    onSuccess: (_result, variables) => {
      queryClient.invalidateQueries({ queryKey: ['collection-achievements', variables.collectionId] })
      queryClient.invalidateQueries({ queryKey: ['collection-achievement', variables.collectionId, variables.achievementId] })
      queryClient.invalidateQueries({ queryKey: ['collection', variables.collectionId] })
    },
  })
}

/**
 * Delete a collection achievement
 */
export function useDeleteCollectionAchievement() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({
      collectionId,
      achievementId,
    }: {
      collectionId: string
      achievementId: string
    }) => adventureCollectionsApi.deleteAchievement(collectionId, achievementId),
    onSuccess: (_result, variables) => {
      queryClient.invalidateQueries({ queryKey: ['collection-achievements', variables.collectionId] })
      queryClient.invalidateQueries({ queryKey: ['collection', variables.collectionId] })
    },
  })
}

/**
 * Add an adventure to a collection achievement
 */
export function useAddAchievementAdventure() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({
      collectionId,
      achievementId,
      data,
    }: {
      collectionId: string
      achievementId: string
      data: AddAchievementAdventureDto
    }) => adventureCollectionsApi.addAchievementAdventure(collectionId, achievementId, data),
    onSuccess: (_result, variables) => {
      queryClient.invalidateQueries({ queryKey: ['collection-achievements', variables.collectionId] })
      queryClient.invalidateQueries({ queryKey: ['collection-achievement', variables.collectionId, variables.achievementId] })
      queryClient.invalidateQueries({ queryKey: ['collection', variables.collectionId] })
    },
  })
}

/**
 * Remove an adventure from a collection achievement
 */
export function useRemoveAchievementAdventure() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({
      collectionId,
      achievementId,
      adventureId,
    }: {
      collectionId: string
      achievementId: string
      adventureId: string
    }) => adventureCollectionsApi.removeAchievementAdventure(collectionId, achievementId, adventureId),
    onSuccess: (_result, variables) => {
      queryClient.invalidateQueries({ queryKey: ['collection-achievements', variables.collectionId] })
      queryClient.invalidateQueries({ queryKey: ['collection-achievement', variables.collectionId, variables.achievementId] })
      queryClient.invalidateQueries({ queryKey: ['collection', variables.collectionId] })
    },
  })
}

// ===================
// USER ACHIEVEMENT PROGRESS HOOKS
// ===================

/**
 * Get current user's progress on a specific achievement
 */
export function useAchievementProgress(achievementId: string | undefined) {
  return useQuery({
    queryKey: ['achievement-progress', achievementId],
    queryFn: () => adventureCollectionsApi.getAchievementProgress(achievementId!),
    enabled: !!achievementId,
  })
}

/**
 * Get current user's progress on all collection achievements
 */
export function useMyAchievementProgress() {
  return useQuery({
    queryKey: ['my-achievement-progress'],
    queryFn: () => adventureCollectionsApi.getMyAchievementProgress(),
  })
}

/**
 * Get compact progress summaries for current user
 */
export function useMyAchievementProgressSummary() {
  return useQuery({
    queryKey: ['my-achievement-progress-summary'],
    queryFn: () => adventureCollectionsApi.getMyAchievementProgressSummary(),
  })
}

/**
 * Get list of achievement IDs the current user has completed
 */
export function useCompletedAchievementIds() {
  return useQuery({
    queryKey: ['completed-achievement-ids'],
    queryFn: () => adventureCollectionsApi.getCompletedAchievementIds(),
  })
}

// ===================
// COLLECTION MAINTAINER HOOKS
// ===================

/**
 * Fetch all maintainers for a collection
 */
export function useCollectionMaintainers(collectionId: string | undefined) {
  return useQuery({
    queryKey: ['collection-maintainers', collectionId],
    queryFn: () => adventureCollectionsApi.getMaintainers(collectionId!),
    enabled: !!collectionId,
  })
}

/**
 * Add a maintainer to a collection
 */
export function useAddCollectionMaintainer() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({
      collectionId,
      data,
    }: {
      collectionId: string
      data: AddCollectionMaintainerRequest
    }) => adventureCollectionsApi.addMaintainer(collectionId, data),
    onSuccess: (_result, variables) => {
      queryClient.invalidateQueries({ queryKey: ['collection-maintainers', variables.collectionId] })
    },
  })
}

/**
 * Remove a maintainer from a collection
 */
export function useRemoveCollectionMaintainer() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({
      collectionId,
      maintainerId,
    }: {
      collectionId: string
      maintainerId: string
    }) => adventureCollectionsApi.removeMaintainer(collectionId, maintainerId),
    onSuccess: (_result, variables) => {
      queryClient.invalidateQueries({ queryKey: ['collection-maintainers', variables.collectionId] })
    },
  })
}
